import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAuth } from "../../hooks/useAuth";
import { usePermissions } from "../../hooks/usePermissions";
import { FiPlus, FiDownload, FiTrash2, FiFolder, FiUser, FiBriefcase, FiMoreVertical } from "react-icons/fi";
import LeadsPageHeader from "./components/LeadsPageHeader";
import LeadsTable from "./components/LeadsTable";
import LeadsTableShimmer from "./components/LeadsTableShimmer";
import LeadsPagination from "./components/LeadsPagination";
import LeadsColumnEditor from "./components/LeadsColumnEditor";
import LeadActionsMenu from "./components/LeadActionsMenu";
import LeadImportFlow from "./components/LeadImportFlow";
import AddLeadModal from "./components/AddLeadModal";
import AssignLeadModal from "./components/AssignLeadModal";
import LeadsFilterDrawer from "./components/LeadsFilterDrawer";
import LeadsDashboard from "./components/LeadsDashboard";
import SearchableDropdown from "../../components/shared/dropdown/SearchableDropdown";
import { useLeadsData } from "./hooks/useLeadsData";
import { useCreateLead, useUpdateLead, useDeleteLead, useGetLeadStats, useGetLeads, useBulkUpdateLeads, useBulkDeleteLeads } from "./api";
import { useSyncAllCampaignLeads } from "../../api/campaignDetails";
import { useCompanyActiveProjects, useGetAllEmployees } from "../../api/hooks";
import { useGetClientSalesTeam } from "../../api/clientSalesTeam";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { format } from "date-fns";
import socketService from "../../services/socketService";

const STORAGE_KEY = "leads-column-visibility";

// Helper functions for localStorage persistence
const loadColumnVisibility = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Ensure it's an object
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return parsed;
    }
    return null;
  } catch (error) {
    console.error("Error loading column visibility:", error);
    return null;
  }
};

const saveColumnVisibility = (columns) => {
  try {
    const visibilityMap = {};
    columns.forEach((col) => {
      visibilityMap[col.key] = col.visible;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibilityMap));
  } catch (error) {
    console.error("Error saving column visibility:", error);
  }
};

const clearColumnVisibility = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing column visibility:", error);
  }
};

const LeadsFeature = ({
  onSelectLead,
  onOpenSettings,
  projectId,
  isFollowUpOnly = false,
  branchFilter = "",
  branches = [],
  projectFilter = "",
  isClient = false,
  onBranchFilterChange,
  onProjectFilterChange,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: projects = [] } = useCompanyActiveProjects();
  const { data: employeesData } = useGetAllEmployees(!isClient);
  const { data: salesTeamData } = useGetClientSalesTeam(projectId || null);
  const rawOwnersList = (isClient || projectId) ? (salesTeamData?.data || []) : (employeesData?.employees || []);
  
  // Use local state if prop is not provided
  const [branchFilterState, setBranchFilterState] = useState("");
  const activeBranchFilter = branchFilter || branchFilterState;

  // Filter owners list by branch if a branch filter is applied
  const ownersList = useMemo(() => {
    if (!activeBranchFilter) return rawOwnersList;
    return rawOwnersList.filter(owner => {
      // Depending on the model, branch might be nested or direct
      const ownerBranch = owner.branch || owner.customFields?.branch;
      return ownerBranch === activeBranchFilter;
    });
  }, [rawOwnersList, activeBranchFilter]);



  const { hasPermission } = usePermissions();
  const isAdmin = user?.role === "company-admin";
  const isEmployee = user?.role === "employee";
  // Check if current user is client (either from prop or from user role)
  const isUserClient = isClient || user?.role === "client";
  const canManageAllLeads = (hasPermission("leads", "viewAll")) && !isUserClient;
  const canAddLead = hasPermission("leads", "create") || (isUserClient && user?.permissions?.includes("add_lead"));
  const canEditLead = hasPermission("leads", "edit") || hasPermission("tasks", "editLead");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [columns, setColumns] = useState([]);
  const [isColumnEditorOpen, setColumnEditorOpen] = useState(false);
  const [isLeadMenuOpen, setLeadMenuOpen] = useState(false);
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const [isAddLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [columnDraft, setColumnDraft] = useState([]);
  const hasInitializedColumns = useRef(false);
  const lastGeneratedColumnsKeys = useRef(null);
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedLeadForAssign, setSelectedLeadForAssign] = useState(null);
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [isMobileGlobalFiltersOpen, setIsMobileGlobalFiltersOpen] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true);
  const [isFromInsights, setIsFromInsights] = useState(false);
  const [isBulkMoreMenuOpen, setBulkMoreMenuOpen] = useState(false);
  const [isBulkBranchMenuOpen, setBulkBranchMenuOpen] = useState(false);
  const [isBulkProjectMenuOpen, setBulkProjectMenuOpen] = useState(false);
  const [bulkBarPos, setBulkBarPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [tempDateRange, setTempDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [projectFilterState, setProjectFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('project') || "";
  });
  const [ownerFilterState, setOwnerFilterState] = useState(() => sessionStorage.getItem('leads_ownerFilter') || "");
  
  const [scoreFilterState, setScoreFilterState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('scoreCategory') || sessionStorage.getItem('leads_scoreFilter') || "";
  });
  
  const [statusCategoryState, setStatusCategoryState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('statusCategory') || sessionStorage.getItem('leads_statusCategory') || "";
  });
  
  const [followUpCountState, setFollowUpCountState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('followUpCount') || sessionStorage.getItem('leads_followUpCount') || "";
  });

  useEffect(() => {
    if (ownerFilterState) sessionStorage.setItem('leads_ownerFilter', ownerFilterState);
    else sessionStorage.removeItem('leads_ownerFilter');
  }, [ownerFilterState]);

  useEffect(() => {
    if (scoreFilterState) sessionStorage.setItem('leads_scoreFilter', scoreFilterState);
    else sessionStorage.removeItem('leads_scoreFilter');
  }, [scoreFilterState]);

  useEffect(() => {
    if (statusCategoryState) sessionStorage.setItem('leads_statusCategory', statusCategoryState);
    else sessionStorage.removeItem('leads_statusCategory');
  }, [statusCategoryState]);

  useEffect(() => {
    if (followUpCountState) sessionStorage.setItem('leads_followUpCount', followUpCountState);
    else sessionStorage.removeItem('leads_followUpCount');
  }, [followUpCountState]);
  const navigate = useNavigate();
  const location = useLocation();



  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Persist states to session storage
  const [activeStatusId, setActiveStatusId] = useState(() => sessionStorage.getItem('leads_activeStatusId') || null);
  const [activeAction, setActiveAction] = useState(() => sessionStorage.getItem('leads_activeAction') || null);
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const saved = sessionStorage.getItem(`leads_appliedFilters_${projectId}`);
    return saved ? JSON.parse(saved) : {};
  });
  const [statsDateRange, setStatsDateRange] = useState(() => {
    const saved = sessionStorage.getItem('leads_statsDateRange');
    return saved ? JSON.parse(saved) : { startDate: '', endDate: '', preset: '' };
  });

  useEffect(() => {
    if (activeStatusId) sessionStorage.setItem('leads_activeStatusId', activeStatusId);
    else sessionStorage.removeItem('leads_activeStatusId');
  }, [activeStatusId]);

  useEffect(() => {
    if (activeAction) sessionStorage.setItem('leads_activeAction', activeAction);
    else sessionStorage.removeItem('leads_activeAction');
  }, [activeAction]);

  useEffect(() => {
    sessionStorage.setItem(`leads_appliedFilters_${projectId}`, JSON.stringify(appliedFilters));
  }, [appliedFilters, projectId]);

  useEffect(() => {
    sessionStorage.setItem('leads_statsDateRange', JSON.stringify(statsDateRange));
  }, [statsDateRange]);

  // Compute branches to show based on selected project
  const actualBranches = useMemo(() => {
    let rawBranches = [];
    
    if (branches && branches.length > 0) {
      rawBranches = branches;
    } else if (!projectId) {
      const activeProjectId = projectFilterState || projectFilter || projectId;
      if (activeProjectId && projects && projects.length > 0) {
        const selectedProject = projects.find(p => (p.id === activeProjectId || p._id === activeProjectId));
        if (selectedProject?.customFields?.branchLogins && Array.isArray(selectedProject.customFields.branchLogins) && selectedProject.customFields.branchLogins.length > 0) {
          rawBranches = selectedProject.customFields.branchLogins;
        }
      }
    }
    
    if (rawBranches && rawBranches.length > 0) {
      const validBranches = rawBranches.filter(b => b && (typeof b === 'string' ? b.trim() !== '' : true));
      if (validBranches.length > 0) return validBranches;
    }
    
    return [];
  }, [branches, projectFilterState, projectFilter, projectId, projects]);

  const branchOptions = useMemo(() => {
    if (!actualBranches) return [];
    const options = actualBranches.map(b => {
      const branchName = typeof b === "string" ? b : b.name;
      return { label: branchName, value: branchName };
    });
    // Add "Unknown" option
    options.push({ label: "Unknown / No Branch", value: "unknown" });
    return options;
  }, [actualBranches]);

  // Stats
  const { data: statsData, isLoading: statsLoading } = useGetLeadStats({
    days: "all",
    project: projectFilterState || projectFilter || projectId,
    timezoneOffset: new Date().getTimezoneOffset(),
    ...(statsDateRange.startDate && statsDateRange.endDate ? {
      startDate: statsDateRange.startDate,
      endDate: statsDateRange.endDate
    } : {}),
    ...(activeBranchFilter ? { branch: activeBranchFilter } : {}),
    ...(ownerFilterState ? { owner: ownerFilterState } : {}),
    ...(scoreFilterState ? { scoreCategory: scoreFilterState } : {}),
    ...(isFollowUpOnly ? { isFollowUp: true } : {})
  });



  const leadStats = useMemo(() => {
    return statsData?.data;
  }, [statsData]);


  // Mutations
  const updateLeadMutation = useUpdateLead();
  const deleteLeadMutation = useDeleteLead();
  const bulkUpdateMutation = useBulkUpdateLeads();
  const bulkDeleteMutation = useBulkDeleteLeads();
  const syncAllCampaignLeadsMutation = useSyncAllCampaignLeads();

  const [searchParams, setSearchParams] = useState(new URLSearchParams(window.location.search));

  // Handle URL filters on mount
  useEffect(() => {
    const scheduled = searchParams.get('scheduled');
    const minScore = searchParams.get('minScore');
    const ownerId = searchParams.get('owner');

    // Check for follow-up filter parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    const statusParam = urlParams.get('status');
    const startDateParam = urlParams.get('startDate');
    const endDateParam = urlParams.get('endDate');
    const filterField = urlParams.get('filterField');
    const filterValue = urlParams.get('filterValue');
    const projectParam = urlParams.get('project');
    const campaignParam = urlParams.get('campaign');

    const scheduledParam = urlParams.get('scheduled');
    const createdParam = urlParams.get('created');
    const branchParam = urlParams.get('branch');

    const hasDashboardParams = createdParam || action ||minScore || ownerId || statusParam || (startDateParam && endDateParam) || (filterField && filterValue) || projectParam || campaignParam || scheduledParam;
    
    let newFilters = {};

    if (hasDashboardParams) {
      // Clear any stale custom filters when navigating from dashboard
      setIsFromInsights(true);
      setActiveStatusId(null);
      setScoreFilterState("");
      setStatusCategoryState("");
      setFollowUpCountState("");
      setShowDashboard(false);
    }
    
    if (scheduledParam === 'today') {
      setStatsDateRange({ startDate: '', endDate: '', preset: 'all' });
      newFilters['scheduled'] = { operator: 'today', value: '' };
      toast.success("Showing today's follow-ups");
    }

    if (createdParam === 'today') {
      setStatsDateRange({ startDate: '', endDate: '', preset: 'all' });
      newFilters['createdAt'] = { operator: 'today', value: 'today' };
      toast.success("Showing today's new leads");
    }

    if (branchParam) {
      setBranchFilterState(branchParam);
    }

    if (action === 'followup') {
      setActiveAction('followup');
      setStatsDateRange({ preset: '', startDate: '', endDate: '' });
      toast.success("Showing all follow-ups");
    } else if (minScore) {
      newFilters['score'] = { operator: 'greaterThan', value: minScore };
      setActiveAction('hot');
      toast.success(`Showing leads with score > ${minScore}`);
    } else if (ownerId) {
      newFilters['owner'] = { operator: 'equals', value: ownerId };
      toast.success("Filtering leads by assigned employee");
    }

    if (statusParam) {
      setActiveStatusId(statusParam);
      toast.success("Filtering leads by status");
    }

    if (startDateParam && endDateParam) {
      setStatsDateRange({
        startDate: startDateParam,
        endDate: endDateParam,
        preset: 'custom'
      });
      toast.success("Filtering leads by date range");
    }

    if (filterField && filterValue) {
      newFilters[filterField] = { operator: 'equals', value: filterValue };
    }
    if (campaignParam && campaignParam !== 'all') {
      newFilters['campaign'] = { operator: 'equals', value: campaignParam };
    }
    
    if (hasDashboardParams) {
      setAppliedFilters(newFilters);
    }

    if (projectParam) {
      setProjectFilter(projectParam);
    }

    // Clear params from URL to avoid re-applying on refresh if not desired, 
    // but typically we keep them. User asked to "list the lead who need to follow up"
    // so applying it on mount is better.
  }, []);

  // ─── Real-time: Subscribe to new lead events via Socket.IO ───────────────
  useEffect(() => {
    const companyId = user?.company;
    if (!companyId) return;

    const companyRoom = `company_${companyId}`;

    // Join the company-level room so the backend can target us
    socketService.joinRoom(companyRoom);

    // Also join the project room if a project is selected
    const activeProjectId = projectFilterState || projectFilter || projectId;
    if (activeProjectId) {
      socketService.joinRoom(`project_${activeProjectId}`);
    }

    // Handler: a new lead arrived (from FB webhook / WhatsApp / manual creation)
    const handleNewLead = (data) => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadStats"]);
      const leadName = data?.contact?.name || "New lead";
      toast.success(`🎯 ${leadName} just came in!`, { duration: 4000 });
    };

    // Handler: global company notification (campaignName + leadName)
    const handleNewLeadReceived = (data) => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadStats"]);
      if (data?.leadName && data?.campaignName) {
        toast.success(`📥 ${data.leadName} via "${data.campaignName}"`, { duration: 4000 });
      }
    };

    socketService.onNewLead(handleNewLead);
    socketService.onNewLeadReceived(handleNewLeadReceived);

    return () => {
      socketService.offNewLead(handleNewLead);
      socketService.offNewLeadReceived(handleNewLeadReceived);
      socketService.leaveRoom(companyRoom);
      if (activeProjectId) {
        socketService.leaveRoom(`project_${activeProjectId}`);
      }
    };
  }, [user?.company, projectFilterState, projectFilter, projectId, queryClient]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when branch filter changes
  useEffect(() => {
    setPage(1);
  }, [activeBranchFilter]);

  // Reset page when project filter changes
  useEffect(() => {
    setPage(1);
  }, [projectFilterState]);

  // Reset page when stats date range changes
  useEffect(() => {
    setPage(1);
  }, [statsDateRange]);

  // Reset page when owner or score filter changes
  useEffect(() => {
    setPage(1);
  }, [ownerFilterState, scoreFilterState, statusCategoryState, followUpCountState]);

  // Use the custom hook to get leads data
  const {
    leads,
    pagination,
    formFields,
    statuses,
    columns: generatedColumns,
    isLoading,
    refetchLeads,
    isRefetching,
    isFetching
  } = useLeadsData({
    page,
    limit: pageSize,
    search: debouncedSearch,
    status: activeStatusId,
    // Only force own owner if user is employee AND NOT allowed to see all leads
    // Otherwise allow filtering by owner from ownerFilterState or appliedFilters (dashboard click)
    owner: (isEmployee && !canManageAllLeads) ? user._id : (ownerFilterState || appliedFilters.owner?.value || null),
    scoreCategory: scoreFilterState || null,
    statusCategory: statusCategoryState || null,
    followUpCount: followUpCountState || null,
    appliedFilters,
    projectId: projectFilterState || projectFilter || projectId,
    isFollowUp: isFollowUpOnly || activeAction === 'followup',
    branchFilter: activeBranchFilter,
    startDate: statsDateRange.startDate,
    endDate: statsDateRange.endDate,
    timezoneOffset: new Date().getTimezoneOffset(),
    isInfiniteScroll: isClient && isMobile,
  });

  const loadMoreRef = useRef(null);
  const fetchingPageRef = useRef(null);

  useEffect(() => {
    if (!isFetching) {
      fetchingPageRef.current = null;
    }
  }, [isFetching]);

  const handleScroll = useCallback((e) => {
    if (!isClient || !isMobile) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      if (page < pagination?.pages && !isFetching && fetchingPageRef.current !== page) {
        fetchingPageRef.current = page;
        setPage(p => p + 1);
      }
    }
  }, [isClient, isMobile, page, pagination?.pages, isFetching]);
  const handleStatusFilter = (statusId) => {
    setActiveStatusId((prev) => (prev === statusId ? null : statusId));
    setAppliedFilters({});
    setActiveAction(null);
    setPage(1);
  };

  const handleActionFilter = (action) => {
    setActiveAction((prev) => (prev === action ? null : action));
    setActiveStatusId(null);
    setPage(1);

    switch (action) {
      case 'hot':
        setAppliedFilters({ score: { operator: 'greaterThan', value: '70' } });
        break;
      case 'weak':
        setAppliedFilters({ score: { operator: 'lessThan', value: '30' } });
        break;
      case 'followup':
        setAppliedFilters({});
        break;
      case 'today':
        setAppliedFilters({ createdAt: { operator: 'today', value: 'today' } });
        break;
      case 'week':
        setAppliedFilters({ createdAt: { operator: 'last7days', value: 'last7days' } });
        break;
      default:
        setAppliedFilters({});
    }
  };

  const handleDatePresetChange = (e) => {
    const preset = e.target.value;
    
    if (preset === 'custom') {
      setShowCustomDateModal(true);
      return;
    }

    if (!preset) {
      setStatsDateRange({ preset: '', startDate: '', endDate: '' });
      return;
    }

    let start = new Date();
    let end = new Date();

    switch (preset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'this_week':
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        break;
      case 'last_week':
        const lastWeekEnd = new Date(start);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - lastWeekEnd.getDay());
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekStart.getDate() - 6);
        start = lastWeekStart;
        end = lastWeekEnd;
        break;
      case 'this_month':
        start.setDate(1);
        break;
      case 'last_month':
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        end.setDate(0);
        break;
      default:
        break;
    }

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${d}`;
    };

    setStatsDateRange({
      preset,
      startDate: formatDate(start),
      endDate: formatDate(end)
    });
  };

  const handleApplyCustomDate = () => {
    const start = tempDateRange[0].startDate;
    const end = tempDateRange[0].endDate;
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${d}`;
    };
    setStatsDateRange({
      preset: 'custom',
      startDate: formatDate(start),
      endDate: formatDate(end)
    });
    setShowCustomDateModal(false);
  };

  // Initialize columns from generated columns
  useEffect(() => {
    // Only initialize if we have columns
    if (generatedColumns.length > 0) {
      const currentKeys = generatedColumns.map((c) => c.key).join(",");

      setColumns((prev) => {
        // On first initialization (when columns are empty or we haven't loaded from localStorage yet), always check localStorage
        if (prev.length === 0 || !hasInitializedColumns.current) {
          const savedVisibility = loadColumnVisibility();
          const initializedColumns = generatedColumns.map((col) => {
            // Use saved visibility if available, otherwise use default
            if (
              savedVisibility &&
              col.key in savedVisibility &&
              typeof savedVisibility[col.key] === "boolean"
            ) {
              return { ...col, visible: savedVisibility[col.key] };
            }
            return col;
          });
          setColumnDraft(initializedColumns);
          hasInitializedColumns.current = true;
          lastGeneratedColumnsKeys.current = currentKeys;
          return initializedColumns;
        }

        // If generated columns keys haven't changed, don't update (prevents unnecessary re-renders)
        if (lastGeneratedColumnsKeys.current === currentKeys) {
          return prev;
        }

        // If we already have columns, we need to merge in any new columns
        // while preserving the user's visibility choices
        const newColumnKeys = new Set(generatedColumns.map((c) => c.key));
        const prevColumnKeys = new Set(prev.map((c) => c.key));

        // Check if there are any changes
        const hasNewColumns = generatedColumns.some(
          (col) => !prevColumnKeys.has(col.key)
        );

        // If keys match exactly, no need to update (prevents loops)
        if (!hasNewColumns && prev.length === generatedColumns.length) {
          lastGeneratedColumnsKeys.current = currentKeys;
          return prev;
        }

        // Merge logic: Keep existing visibility for known columns, add new columns as hidden (or default)
        const mergedColumns = generatedColumns.map((newCol) => {
          const existing = prev.find((p) => p.key === newCol.key);
          if (existing) {
            return { ...newCol, visible: existing.visible };
          }
          return newCol;
        });

        setColumnDraft(mergedColumns);
        lastGeneratedColumnsKeys.current = currentKeys;
        return mergedColumns;
      });
    }
  }, [generatedColumns]);

  // Create lead mutation
  const { mutate: createLead, isLoading: isCreatingLead } = useCreateLead();

  const visibleLeads = useMemo(() => {
    return leads;
  }, [leads]);


  const handleToggleSelect = (leadId) => {
    setSelectedLeadIds((prev) => {
      const stringId = String(leadId);
      return prev.includes(stringId)
        ? prev.filter((id) => id !== stringId)
        : [...prev, stringId];
    });
  };

  const handleToggleSelectAll = (shouldSelect, leadIds) => {
    setSelectedLeadIds((prev) => {
      const stringIds = leadIds.map((id) => String(id));
      if (shouldSelect) {
        const uniqueIds = new Set([...prev, ...stringIds]);
        return Array.from(uniqueIds);
      }
      return prev.filter((id) => !stringIds.includes(id));
    });
  };

  const handleAction = (action) => () => {
    if (action === "add-lead") {
      setAddLeadModalOpen(true);
    } else if (action === "add-filter") {
      setFilterDrawerOpen(true);
    } else if (action === "refresh") {
      refetchLeads();
      syncAllCampaignLeadsMutation.mutate(null, {
        onSuccess: (data) => {
          toast.success(data?.message || "Campaign sync started in background");
          refetchLeads();
        },
        onError: (err) => {
          console.error("[Sync All] Error:", err);
          toast.error(err?.response?.data?.message || "Failed to sync campaign leads");
        }
      });
    } else {
      console.info(`Leads action triggered: ${action}`);
    }
  };

  const handleLeadCreated = () => {
    refetchLeads(); // Reload leads after creating a new one
    queryClient.invalidateQueries(["leads"]);
  };

  const openColumnEditor = () => {
    setColumnDraft(columns.map((column) => ({ ...column })));
    setColumnEditorOpen(true);
  };

  const handleColumnToggle = (key) => {
    setColumnDraft((prev) => {
      const visibleCount = prev.filter((column) => column.visible).length;
      return prev.map((column) => {
        if (column.key === key) {
          if (column.visible && visibleCount === 1) {
            return column;
          }
          return { ...column, visible: !column.visible };
        }
        return column;
      });
    });
  };

  const resetColumns = () => {
    // Reset to default system columns visibility
    const defaultColumns = generatedColumns.map((col) => ({
      ...col,
      visible:
        col.isSystem &&
        [
          "createdAt",
          "contact.name",
          "name",
          "status",
          "score",
          "contact.phone",
          "phone",
        ].includes(col.key),
    }));
    setColumnDraft(defaultColumns);
    // Clear saved visibility when resetting
    clearColumnVisibility();
  };

  const applyColumns = () => {
    setColumns(columnDraft);
    // Save column visibility to localStorage
    saveColumnVisibility(columnDraft);
    setColumnEditorOpen(false);
  };

  const closeColumnEditor = () => setColumnEditorOpen(false);
  const openLeadMenu = () => setLeadMenuOpen(true);
  const closeLeadMenu = () => setLeadMenuOpen(false);

  const clientProjectId = user?.projectId || (typeof user?.project === "string" ? user.project : user?.project?._id);
  const importProjectId = String(
    projectId || (isUserClient ? clientProjectId : "") || projectFilterState || projectFilter || ""
  );

  const openUploadModal = () => {
    setLeadMenuOpen(false);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
  };

  const canToggleColumn =
    columnDraft.filter((column) => column.visible).length > 1;

  // Action handlers
  const handleEdit = (lead) => {
    const leadId = lead._id || lead.id;
    if (leadId) {
      navigate(`/leads/${leadId}`, { state: lead });
    }
  };

  const handleSendEmail = (lead) => {
    const email = lead.contact?.email || lead.email;
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      toast.error("No email address found for this lead");
    }
  };

  const handleCreateTask = (lead) => {
    // Navigate to create task page with lead context
    navigate("/board", { state: { leadContext: lead } });
    toast.success("Redirecting to create task...");
  };

  const handleAssign = (lead) => {
    setSelectedLeadForAssign(lead);
    setAssignModalOpen(true);
  };

  const handleAssignConfirm = async (assigneeId, assignType) => {
    if (!selectedLeadForAssign) return;

    try {
      const updatePayload = assignType === "salesPerson"
        ? { clientOwner: assigneeId }
        : { owner: assigneeId };

      if (selectedLeadForAssign === 'bulk') {
        if (selectedLeadIds.length === 0) return;
        await bulkUpdateMutation.mutateAsync({
          leadIds: selectedLeadIds,
          updateData: updatePayload,
        });
        toast.success(assignType === "salesPerson" ? `Assigned ${selectedLeadIds.length} leads to sales team member` : `Assigned ${selectedLeadIds.length} leads to owner`);
        setSelectedLeadIds([]);
      } else {
        const leadId = selectedLeadForAssign._id || selectedLeadForAssign.id;
        await updateLeadMutation.mutateAsync({
          leadId,
          leadData: updatePayload,
        });
        toast.success(assignType === "salesPerson" ? "Lead assigned to sales team member" : "Lead owner updated successfully");
      }
      
      setAssignModalOpen(false);
      setSelectedLeadForAssign(null);
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to assign lead(s)");
    }
  };

  // Wrapper so handleAssignConfirm receives (id, type) from the modal
  const handleAssignWithType = (assigneeId, assignType) => handleAssignConfirm(assigneeId, assignType);

  const handleDelete = async (lead) => {
    const leadName = lead.contact?.name || lead.name || "this lead";
    if (
      window.confirm(
        `Are you sure you want to delete ${leadName}? This action cannot be undone.`
      )
    ) {
      try {
        const leadId = lead._id || lead.id;
        await deleteLeadMutation.mutateAsync(leadId);
        toast.success("Lead deleted successfully");
        refetchLeads();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to delete lead");
      }
    }
  };

  const handleConvert = (lead) => {
    // TODO: Implement convert lead to customer/opportunity
    toast.info("Convert functionality coming soon");
  };

  const handleCopyURL = (lead) => {
    const leadId = lead._id || lead.id;
    const url = `${window.location.origin}/leads/${leadId}`;
    navigator.clipboard.writeText(url);
    toast.success("Lead URL copied to clipboard");
  };

  const handleStatusChange = async (lead, statusId) => {
    try {
      const leadId = lead._id || lead.id;
      await updateLeadMutation.mutateAsync({
        leadId,
        leadData: { status: statusId },
      });
      toast.success("Lead status updated successfully");
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  const handleSourceChange = async (lead, source) => {
    try {
      const leadId = lead._id || lead.id;
      await updateLeadMutation.mutateAsync({
        leadId,
        leadData: { source: source },
      });
      toast.success("Lead source updated successfully");
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update source");
    }
  };

  const handleCustomFieldChange = async (lead, fieldKey, newValue) => {
    try {
      const leadId = lead._id || lead.id;

      // Update the customFields object
      const updatedCustomFields = {
        ...(lead.customFields || {}),
        [fieldKey]: newValue,
      };

      await updateLeadMutation.mutateAsync({
        leadId,
        leadData: { customFields: updatedCustomFields },
      });

      toast.success("Field updated successfully");
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update field");
    }
  };

  const handleMoveToBranch = async (lead, branchName) => {
    try {
      const leadId = lead._id || lead.id;
      const updatedCustomFields = {
        ...(lead.customFields || {}),
        branch: branchName,
      };

      await updateLeadMutation.mutateAsync({
        leadId,
        leadData: {
          branch: branchName,
          customFields: updatedCustomFields
        },
      });

      toast.success(`Lead moved to branch: ${branchName}`);
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to move lead to branch");
    }
  };

  const handleMoveToProject = async (lead, targetProjectId, targetProjectName) => {
    try {
      const leadId = lead._id || lead.id;

      await updateLeadMutation.mutateAsync({
        leadId,
        leadData: {
          project: targetProjectId,
        },
      });

      toast.success(`Lead moved to project: ${targetProjectName}`);
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["projectDetails", projectId]);
      queryClient.invalidateQueries(["projectDetails", targetProjectId]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to move lead to project");
    }
  };

  const handleBulkMoveToBranch = async (branchName) => {
    if (selectedLeadIds.length === 0) return;

    try {
      await bulkUpdateMutation.mutateAsync({
        leadIds: selectedLeadIds,
        updateData: {
          branch: branchName,
          "customFields.branch": branchName
        },
      });

      toast.success(`Moved ${selectedLeadIds.length} leads to branch: ${branchName}`);
      setSelectedLeadIds([]);
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to move leads to branch");
    }
  };

  const handleBulkMoveToProject = async (targetProjectId) => {
    if (selectedLeadIds.length === 0) return;

    try {
      await bulkUpdateMutation.mutateAsync({
        leadIds: selectedLeadIds,
        updateData: {
          project: targetProjectId
        },
      });

      toast.success(`Moved ${selectedLeadIds.length} leads to project`);
      setSelectedLeadIds([]);
      setBulkProjectMenuOpen(false);
      refetchLeads();
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["projectDetails"]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to move leads to project");
    }
  };

  const handleDragStart = (e) => {
    // Only drag if left clicking on the bar (not buttons)
    if (e.button !== 0) return;

    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - bulkBarPos.x,
      y: e.clientY - bulkBarPos.y,
    };

    // Prevent text selection during drag
    e.preventDefault();
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;

    setBulkBarPos({
      x: e.clientX - dragStartPos.current.x,
      y: e.clientY - dragStartPos.current.y,
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
    } else {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    setPage(1); // Reset to first page when filters change
    const filterCount = Object.keys(filters).length;
    if (filterCount > 0) {
      toast.success(`${filterCount} filter(s) applied successfully`);
    } else {
      toast.info("All filters cleared");
    }
  };

  const handleDownloadTemplate = () => {
    // Generate content from formFields
    let fieldsToUse = formFields;

    // Fallback if no form fields are loaded (mirrors AddLeadModal fallback)
    if (!fieldsToUse || fieldsToUse.length === 0) {
      fieldsToUse = [
        { label: "Full Name", key: "system_name" },
        { label: "Email", key: "system_email" },
        { label: "Phone Number", key: "system_phone" },
        { label: "Company", key: "company" },
      ];
    }

    // Use labels as headers, fallback to keys if label is missing
    const headers = fieldsToUse.map((field) => field.label || field.key);

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers]); // Add headers as first row

    // Adjust column widths based on header length (optional polish)
    const wscols = headers.map(h => ({ wch: h.length + 5 }));
    ws['!cols'] = wscols;

    XLSX.utils.book_append_sheet(wb, ws, "Leads Template");

    // Generate Excel file and trigger download
    XLSX.writeFile(wb, "lead_import_template.xlsx");
  };

  const globalFiltersContent = (
    <>
      {/* Date Filter */}
      <div className="flex flex-col gap-1 flex-1 min-w-[130px]">
        <span className="block text-[10px] font-medium text-slate-500 uppercase tracking-widest md:hidden mb-0.5">
          Date Range
        </span>
        <span className="hidden md:block text-[9px] font-medium text-slate-400 uppercase tracking-widest">
          Date Range
        </span>
        <div className="flex items-center gap-1">
          <select
            value={statsDateRange?.preset || ''}
            onChange={handleDatePresetChange}
            className="w-full px-3 md:px-2 py-1.5 border border-slate-200 rounded-xl text-xs font-bold focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none cursor-pointer bg-slate-50/50 hover:bg-white transition-all duration-300 text-slate-700"
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="custom">
              {statsDateRange?.preset === 'custom' && statsDateRange?.startDate && statsDateRange?.endDate
                ? `${format(new Date(statsDateRange.startDate), 'MMM d')} - ${format(new Date(statsDateRange.endDate), 'MMM d')}`
                : "Custom Date Range"}
            </option>
          </select>
          {statsDateRange?.preset === 'custom' && (
            <button
              onClick={() => setShowCustomDateModal(true)}
              className="p-2 md:p-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg transition-colors border border-indigo-100"
              title="Edit Custom Date Range"
            >
              <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Branch Filter */}
      {actualBranches && actualBranches.length > 0 && (
        <div className="flex flex-col gap-1 md:pl-2 flex-1 min-w-[130px]">
          <span className="block text-[10px] font-medium text-slate-500 uppercase tracking-widest md:hidden mb-0.5">
            Branch View
          </span>
          <span className="hidden md:block text-[9px] font-medium text-slate-400 uppercase tracking-widest">
            Branch View
          </span>
          <SearchableDropdown
            value={activeBranchFilter || ""}
            onChange={(val) => {
              if (onBranchFilterChange) {
                onBranchFilterChange(val);
              } else {
                setBranchFilterState(val);
              }
            }}
            options={branchOptions}
            placeholder="Global Overview"
            className="w-full text-xs"
          />
        </div>
      )}

      {/* Project Filter */}
      {!isClient && !projectId && (
        <div className="flex flex-col gap-1 md:pl-2 flex-1 min-w-[130px]">
          <span className="block text-[10px] font-medium text-slate-500 uppercase tracking-widest md:hidden mb-0.5">
            Project View
          </span>
          <span className="hidden md:block text-[9px] font-medium text-slate-400 uppercase tracking-widest">
            Project View
          </span>
          <select
            value={projectFilterState || ""}
            onChange={(e) => {
              setProjectFilter(e.target.value);
              setBranchFilterState(""); // Reset branch when project changes
              if (onProjectFilterChange) onProjectFilterChange(e.target.value);
            }}
            className="w-full px-3 md:px-2 py-1.5 border border-slate-200 rounded-xl text-xs font-bold focus:ring-4 focus:ring-green-500/10 focus:border-green-500 outline-none cursor-pointer bg-slate-50/50 hover:bg-white transition-all duration-300 text-slate-700"
          >
            <option value="">All Projects</option>
            {projects.map(p => (
              <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Owner Filter */}
      {ownersList && ownersList.length > 0 && (
        <div className="flex flex-col gap-1 md:pl-2 flex-1 min-w-[130px]">
          <span className="block text-[10px] font-medium text-slate-500 uppercase tracking-widest md:hidden mb-0.5">
            Owner
          </span>
          <span className="hidden md:block text-[9px] font-medium text-slate-400 uppercase tracking-widest">
            Owner
          </span>
          <select
            value={ownerFilterState}
            onChange={(e) => setOwnerFilterState(e.target.value)}
            className="w-full px-3 md:px-2 py-1.5 border border-slate-200 rounded-xl text-xs font-bold focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none cursor-pointer bg-slate-50/50 hover:bg-white transition-all duration-300 text-slate-700"
          >
            <option value="">All Owners</option>
            {ownersList.map(u => {
              const id = u._id || u.id;
              const name = (isClient || projectId) ? u.name : (`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.name || "Unknown");
              return <option key={id} value={id}>{name}</option>;
            })}
          </select>
        </div>
      )}
      
      {/* Score Filter */}
      <div className="flex flex-col gap-1 md:pl-2 flex-1 min-w-[130px]">
        <span className="block text-[10px] font-medium text-slate-500 uppercase tracking-widest md:hidden mb-0.5">
          Score
        </span>
        <span className="hidden md:block text-[9px] font-medium text-slate-400 uppercase tracking-widest">
          Score
        </span>
        <select
          value={scoreFilterState}
          onChange={(e) => setScoreFilterState(e.target.value)}
          className="w-full px-3 md:px-2 py-1.5 border border-slate-200 rounded-xl text-xs font-bold focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none cursor-pointer bg-slate-50/50 hover:bg-white transition-all duration-300 text-slate-700"
        >
          <option value="">All Scores</option>
          <option value="hot">Hot Leads</option>
          <option value="warm">Warm Leads</option>
          <option value="cold">Cold Leads</option>
        </select>
      </div>
    </>
  );

  return (
    <section className="h-full overflow-hidden">
      <div 
        className="h-full overflow-y-auto scrollbar-hide flex flex-col gap-y-1" 
        id="leads-scroll-container"
        onScroll={handleScroll}
      >

        {/* Global Filters Row */}
        {((branches && branches.length > 0 && !isClient) || true) && (
          <div className="hidden md:flex flex-row overflow-x-auto scrollbar-hide items-center gap-2 sm:gap-3 bg-white p-2 md:p-2.5 px-3 md:px-4 border border-slate-100 rounded-xl shrink-0 animate-in fade-in duration-300 relative z-50 w-full">
            {globalFiltersContent}
          </div>
        )}

        {/* dashboard section  */}
        {showDashboard && (!isFromInsights || Object.keys(appliedFilters).length === 0) && (
          <div className="shrink-0 bg-white rounded-2xl p-2 md:p-3 md:px-4 border border-slate-100 overflow-x-auto touch-pan-x scrollbar-hide">
            <LeadsDashboard
              stats={leadStats}
              isLoading={statsLoading}
              activeStatusId={activeStatusId}
              onStatusClick={handleStatusFilter}
              activeAction={activeAction}
              onActionClick={handleActionFilter}
              statsDateRange={statsDateRange}
              setStatsDateRange={setStatsDateRange}
            />
          </div>
        )}

        {/* Active Insights / Custom Filters Section */}
        {Object.keys(appliedFilters).length > 0 && !activeAction && (
          <div className="shrink-0 bg-indigo-50/50 rounded-2xl p-3 md:px-4 border border-indigo-100 flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5">
                <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtered By:
              </span>
              {Object.entries(appliedFilters).map(([key, filter]) => (
                <div key={key} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-xl border border-indigo-200 shadow-sm">
                  <span className="text-xs font-medium text-indigo-500 uppercase tracking-wider">{key.replace('custom_', '')}:</span>
                  <span className="text-sm font-bold text-slate-700">{filter.value}</span>
                  <button 
                    onClick={() => {
                      setAppliedFilters(prev => {
                        const next = { ...prev };
                        delete next[key];
                        return next;
                      });
                    }}
                    className="ml-1 text-slate-400 hover:text-red-500 transition-colors p-0.5 hover:bg-red-50 rounded-md"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button 
              onClick={() => setAppliedFilters({})}
              className="text-xs font-bold text-indigo-600 hover:text-white px-4 py-2 hover:bg-indigo-600 bg-white rounded-xl shadow-sm border border-indigo-200 transition-all duration-300"
            >
              Clear Filter
            </button>
          </div>
        )}
        <div className="relative bg-white grow shrink-0 rounded-2xl border border-slate-100 pb-2">
          <div className="sticky top-0 z-40 bg-white rounded-t-2xl">
            <LeadsPageHeader
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onAddLead={handleAction("add-lead")}
              onAddFilter={handleAction("add-filter")}
              onRefresh={handleAction("refresh")}
              isRefreshing={isRefetching || syncAllCampaignLeadsMutation.isPending}
              onToggleLayout={openColumnEditor}
              onDownload={handleAction("download")}
              onMoreActions={openLeadMenu}
              showDashboard={showDashboard}
              onToggleDashboard={() => setShowDashboard(!showDashboard)}
              isClient={isClient}
              canAddLead={canAddLead}
              onOpenMobileGlobalFilters={() => setIsMobileGlobalFiltersOpen(true)}
            />
          </div>
          {isLoading ? (
            <LeadsTableShimmer columns={columns.filter((col) => col.visible)} />
          ) : (
            <>
              <div>
                <LeadsTable
                  leads={visibleLeads}
                  columns={columns.filter((col) => col.visible)}
                  selectedLeadIds={selectedLeadIds}
                  onToggleSelect={handleToggleSelect}
                  onToggleSelectAll={handleToggleSelectAll}
                  onRowClick={onSelectLead}
                  onEdit={handleEdit}
                  onSendEmail={handleSendEmail}
                  onCreateTask={handleCreateTask}
                  onAssign={handleAssign}
                  onDelete={handleDelete}
                  onConvert={!isClient ? handleConvert : null}
                  onCopyURL={handleCopyURL}
                  statuses={statuses}
                  onStatusChange={handleStatusChange}
                  onCustomFieldChange={handleCustomFieldChange}
                  onSourceChange={handleSourceChange}
                  onMoveToBranch={handleMoveToBranch}
                  branches={actualBranches}
                  canManage={canEditLead}
                  projects={projects}
                  onMoveToProject={!isClient ? handleMoveToProject : null}
                  scrollContainerId="leads-scroll-container"
                  bottomContent={
                    isClient && isMobile ? (
                      <div ref={loadMoreRef} className="py-6 flex justify-center">
                        {isFetching && (
                          <div className="w-5 h-5 border-2 border-[#3f8cff] border-t-transparent rounded-full animate-spin"></div>
                        )}
                      </div>
                    ) : null
                  }
                />
              </div>
              {!(isClient && isMobile) && (
                <div className="sticky bottom-0 bg-white border-t border-slate-100 p-2 md:p-3 rounded-b-2xl z-30">
                  <LeadsPagination
                    pageSize={pageSize}
                    onPageSizeChange={(newSize) => {
                      setPageSize(newSize);
                      setPage(1); // Reset to first page when changing page size
                    }}
                    currentPage={page}
                    onPageChange={setPage}
                    visibleCount={visibleLeads.length}
                    totalCount={pagination?.total}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Floating Bulk Actions Bar */}
        {selectedLeadIds.length > 0 && (
          <div
            className={`fixed z-50 bg-slate-900 text-white ${
              isMobile ? 'bottom-[90px] right-6 px-4 py-2.5 gap-3' : 'bottom-10 left-1/2 -translate-x-1/2 px-6 py-3 gap-6'
            } rounded-full shadow-2xl flex items-center border border-slate-700 animate-in fade-in slide-in-from-bottom-4 duration-300 ${isDragging ? 'cursor-grabbing scale-105 opacity-90 transition-none' : 'cursor-grab'}`}
            style={{
              transform: isMobile 
                ? `translate(${bulkBarPos.x}px, ${bulkBarPos.y}px)`
                : `translate(calc(-50% + ${bulkBarPos.x}px), ${bulkBarPos.y}px)`,
              transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onMouseDown={handleDragStart}
          >
            <div className={`flex items-center ${isMobile ? 'gap-2 pr-3' : 'gap-3 pr-6'} border-r border-slate-700 pointer-events-none select-none`}>
              <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                {selectedLeadIds.length}
              </span>
              {!isMobile && <span className="text-sm font-medium">Leads Selected</span>}
            </div>

            <div className={`flex items-center ${isMobile ? 'gap-3' : 'gap-4'}`} onMouseDown={(e) => e.stopPropagation()}>
              <div className="relative">
                <button
                  onClick={() => setBulkMoreMenuOpen(!isBulkMoreMenuOpen)}
                  className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-800 transition-colors"
                  title="More Actions"
                >
                  <FiMoreVertical className="w-5 h-5 text-slate-300" />
                </button>

                {isBulkMoreMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-[-1]" onClick={() => { setBulkMoreMenuOpen(false); setBulkBranchMenuOpen(false); setBulkProjectMenuOpen(false); }} />
                    <div className={`absolute bottom-full ${isMobile ? 'right-0' : 'left-0'} mb-2 w-48 bg-slate-800 rounded-xl shadow-2xl border border-slate-700 py-1.5 animate-in fade-in slide-in-from-bottom-2 duration-200 text-slate-200 overflow-hidden`}>
                      
                      <button 
                        onClick={() => { setSelectedLeadForAssign('bulk'); setAssignModalOpen(true); setBulkMoreMenuOpen(false); }} 
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-700 flex items-center gap-3 transition-colors"
                      >
                        <FiUser className="w-4 h-4 text-slate-400" /> Change Owner
                      </button>

                      {/* Move to Branch - Clients can also do this */}
                      {(canEditLead || isClient) && actualBranches && actualBranches.length > 0 && (
                        <div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); setBulkBranchMenuOpen(!isBulkBranchMenuOpen); setBulkProjectMenuOpen(false); }} 
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-700 flex items-center gap-3 transition-colors"
                          >
                            <FiFolder className="w-4 h-4 text-slate-400" /> Move to Branch
                          </button>
                          {isBulkBranchMenuOpen && (
                            <div className="bg-slate-900 border-y border-slate-700 py-1 max-h-[200px] overflow-y-auto scrollbar-hide">
                              {actualBranches.map((branch) => {
                                const branchId = typeof branch === "string" ? branch : (branch.id || branch.name);
                                const branchName = typeof branch === "string" ? branch : branch.name;
                                return (
                                  <button
                                    key={branchId}
                                    onClick={() => { handleBulkMoveToBranch(branchName); setBulkBranchMenuOpen(false); setBulkMoreMenuOpen(false); }}
                                    className="w-full px-11 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 hover:text-blue-400 transition-colors"
                                  >
                                    {branchName}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {!isClient && projects && projects.length > 0 && (canEditLead || isClient) && (
                        <div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); setBulkProjectMenuOpen(!isBulkProjectMenuOpen); setBulkBranchMenuOpen(false); }} 
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-700 flex items-center gap-3 transition-colors"
                          >
                            <FiBriefcase className="w-4 h-4 text-slate-400" /> Move to Project
                          </button>
                          {isBulkProjectMenuOpen && (
                            <div className="bg-slate-900 border-y border-slate-700 py-1 max-h-[200px] overflow-y-auto scrollbar-hide">
                              {projects.map((project) => {
                                const pId = project.id || project._id;
                                const pName = project.name;
                                return (
                                  <button
                                    key={pId}
                                    onClick={() => { handleBulkMoveToProject(pId); setBulkProjectMenuOpen(false); setBulkMoreMenuOpen(false); }}
                                    className="w-full px-11 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 hover:text-blue-400 transition-colors truncate"
                                    title={pName}
                                  >
                                    {pName}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      <button 
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to delete ${selectedLeadIds.length} leads?`)) {
                            try {
                              await bulkDeleteMutation.mutateAsync(selectedLeadIds);
                              toast.success(`Successfully deleted ${selectedLeadIds.length} leads`);
                              setSelectedLeadIds([]);
                              setBulkMoreMenuOpen(false);
                              refetchLeads();
                              queryClient.invalidateQueries(["leads"]);
                            } catch (error) {
                              toast.error(error?.response?.data?.message || "Failed to delete leads");
                            }
                          }
                        }} 
                        disabled={bulkDeleteMutation.isPending}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-700 text-red-400 hover:text-red-300 flex items-center gap-3 border-t border-slate-700 mt-1 transition-colors"
                      >
                        <FiTrash2 className="w-4 h-4" /> {bulkDeleteMutation.isPending ? "Deleting..." : "Delete Leads"}
                      </button>

                    </div>
                  </>
                )}
              </div>

              <button
                onClick={() => setSelectedLeadIds([])}
                className={`text-sm font-medium text-slate-400 hover:text-white transition-colors ${isMobile ? 'pl-2 border-l border-slate-700' : ''}`}
                title="Cancel Selection"
              >
                {isMobile ? "✕" : "Cancel"}
              </button>
            </div>
          </div>
        )}
      </div>

      {showCustomDateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowCustomDateModal(false)}>
          <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-4 w-full text-left">Select Custom Date Range</h3>
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <DateRange
                editableDateInputs={true}
                onChange={item => setTempDateRange([item.selection])}
                moveRangeOnFirstSelection={false}
                ranges={tempDateRange}
                rangeColors={["#3b82f6"]}
                className="font-sans"
              />
            </div>
            <div className="flex gap-3 mt-6 w-full justify-end">
              <button 
                onClick={() => setShowCustomDateModal(false)}
                className="px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleApplyCustomDate}
                className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm hover:shadow transition-all"
              >
                Apply Range
              </button>
            </div>
          </div>
        </div>
      )}

      {isColumnEditorOpen && (
        <div
          className="absolute inset-0 bg-black/0 z-50"
          onClick={closeColumnEditor}
        >
          <div
            className="absolute right-6 top-5"
            onClick={(event) => event.stopPropagation()}
          >
            <LeadsColumnEditor
              columns={columnDraft}
              onToggleColumn={handleColumnToggle}
              onReset={resetColumns}
              onApply={applyColumns}
              onClose={closeColumnEditor}
              canToggleColumn={canToggleColumn}
            />
          </div>
        </div>
      )}

      {isLeadMenuOpen && (
        <div
          className="absolute inset-0 bg-black/0 z-50"
          onClick={closeLeadMenu}
        >
          <div
            className="absolute right-6 top-18"
            onClick={(event) => event.stopPropagation()}
          >
            <LeadActionsMenu
              onClose={closeLeadMenu}
              onUpload={openUploadModal}
              onDownloadTemplate={handleDownloadTemplate}
              onSettings={() => {
                closeLeadMenu();
                onOpenSettings && onOpenSettings();
              }}
            />
          </div>
        </div>
      )}

      <LeadImportFlow
        isOpen={isUploadModalOpen}
        onClose={closeUploadModal}
        projects={projects}
        defaultProjectId={importProjectId}
        isProjectLocked={Boolean(importProjectId) && (Boolean(projectId) || isUserClient)}
        onImported={refetchLeads}
      />

      <AddLeadModal
        isOpen={isAddLeadModalOpen}
        onClose={() => setAddLeadModalOpen(false)}
        onSuccess={handleLeadCreated}
        projectId={projectId}
        branches={actualBranches}
        defaultBranch={branchFilter}
      />

      <AssignLeadModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setSelectedLeadForAssign(null);
        }}
        onAssign={handleAssignWithType}
        currentOwner={selectedLeadForAssign?.owner}
        currentClientOwner={selectedLeadForAssign?.clientOwner}
        isClient={isClient}
        projectId={selectedLeadForAssign?.project?._id || selectedLeadForAssign?.project || projectId}
      />

      <LeadsFilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApplyFilters={handleApplyFilters}
        formFields={formFields}
        statuses={statuses}
        currentFilters={appliedFilters}
        isClient={isClient}
        projectId={projectId}
      />

      {/* Floating Add Lead Button for Mobile / Client Dashboard */}
      {canAddLead && (
        <button
          onClick={() => setAddLeadModalOpen(true)}
          className="fixed bottom-[90px] left-6 z-50 md:hidden flex items-center justify-center bg-[#3f8cff] text-white w-12 h-12 rounded-full shadow-xl hover:bg-[#2f6bff] active:scale-95 transition-all duration-300"
          aria-label="Add lead"
        >
          <FiPlus size={22} />
        </button>
      )}
      {isMobileGlobalFiltersOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div 
            className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setIsMobileGlobalFiltersOpen(false)}
          />
          <div className="fixed right-0 top-0 h-full w-[85vw] max-w-[320px] bg-slate-50 shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out border-l border-slate-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h2 className="text-sm font-bold text-slate-800">Global Overview</h2>
              <button
                onClick={() => setIsMobileGlobalFiltersOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <div className="flex flex-col gap-4">
                {globalFiltersContent}
              </div>
            </div>
            <div className="p-4 bg-white border-t border-slate-200">
              <button 
                onClick={() => setIsMobileGlobalFiltersOpen(false)}
                className="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl shadow-sm hover:bg-blue-700 active:scale-[0.98] transition-all"
              >
                Apply & View Leads
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
};

export default LeadsFeature;
